import React, { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AppBar, Box, Button, Toolbar } from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import logo from '../../assets/Logo.avif';
import { BRAND, frostedSx } from '../../styles/ui';
import ColorModeToggle from './ColorModeToggle';
import QuickExitButton from './QuickExitButton';

export const HEADER_HEIGHT = { xs: 56, sm: 64 };

interface SiteHeaderProps {
  // Page-specific actions, shown just before the colour-mode toggle
  actions?: ReactNode;
}

// One header for every page: logo on the left, page actions, then Quick exit and the
// colour-mode toggle, always in the same top-right spots
const SiteHeader: React.FC<SiteHeaderProps> = ({ actions }) => (
  <AppBar
    position="sticky"
    elevation={0}
    sx={{
      ...frostedSx,
      backgroundColor: BRAND.header,
      color: BRAND.ink,
      borderBottom: `1px solid ${BRAND.line}`,
      pt: 'env(safe-area-inset-top)'
    }}
  >
    <Toolbar disableGutters sx={{ minHeight: HEADER_HEIGHT, px: { xs: 3, md: 5 }, gap: 1 }}>
      <Box component={RouterLink} to="/" aria-label="Joan home" sx={{ display: 'flex', alignItems: 'center', mr: 'auto' }}>
        <Box component="img" src={logo} alt="Joan" sx={{ height: { xs: 29, sm: 34 } }} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
        {actions}
        <QuickExitButton />
        <ColorModeToggle />
      </Box>
    </Toolbar>
  </AppBar>
);

interface HeaderButtonProps {
  to: string;
  icon?: ReactNode;
  // Show only the icon on phones where the header is crowded
  iconOnlyOnPhone?: boolean;
  children: string;
}

// Quiet text button for the header
export const HeaderButton: React.FC<HeaderButtonProps> = ({ to, icon, iconOnlyOnPhone = false, children }) => (
  <Button
    component={RouterLink}
    to={to}
    startIcon={icon}
    aria-label={children}
    sx={{
      color: BRAND.ink,
      px: 2,
      '&:hover': { backgroundColor: BRAND.hover },
      ...(iconOnlyOnPhone && {
        minWidth: { xs: 44, sm: 64 },
        px: { xs: 1, sm: 2 },
        '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 }, ml: { xs: 0, sm: -0.5 } }
      })
    }}
  >
    <Box component="span" sx={iconOnlyOnPhone ? { display: { xs: 'none', sm: 'inline' } } : undefined}>
      {children}
    </Box>
  </Button>
);

export const HomeButton: React.FC<{ iconOnlyOnPhone?: boolean }> = ({ iconOnlyOnPhone }) => (
  <HeaderButton to="/" icon={<HomeOutlinedIcon />} iconOnlyOnPhone={iconOnlyOnPhone}>
    Home
  </HeaderButton>
);

export default SiteHeader;
